import { useEffect, useRef, useState } from 'react';

type Props = {
  /** Target value to count up to. */
  value: number;
  /** Animation length in ms. */
  duration?: number;
  prefix?: string;
  decimals?: number;
  className?: string;
};

/** Lightweight ease-out count-up for a currency value (rAF, no deps).
 *  Animates from the previous value to the new one — so cached data shows
 *  instantly and only a real balance change triggers the animation. */
export function CountUp({ value, duration = 800, prefix = '$', decimals = 2, className }: Props) {
  // Start display at the incoming value (may be from cache → no animation flash).
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const raf = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    fromRef.current = value;

    // Skip animation if value didn't change or the diff is tiny (floating point).
    if (Math.abs(value - from) < 0.001) {
      setDisplay(value);
      return;
    }

    let start = 0;
    cancelAnimationFrame(raf.current);
    const step = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(from + (value - from) * eased);
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);

  const text = display.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span className={className}>
      {prefix}
      {text}
    </span>
  );
}
